//import { Table } from "react-bootstrap";
import React from "react";
import {useCollection} from '@cloudscape-design/collection-hooks';
import {Table, TextFilter, Pagination, CollectionPreferences, PropertyFilter} from '@cloudscape-design/components';
import {useHistory} from "react-router-dom";
import {Formatter} from "../format";
import {Placeholder} from "./Placeholder";
import {SentimentIcon} from "./SentimentIcon";
import {TrendIcon} from "./TrendIcon";
import "./ContactTable.css";
import {DateTimeForm, formatDateTime} from './DateTimeForm';
import {ContactTablePreferences, DEFAULT_PREFERENCES} from './ContactTablePreferences'
import {useLocalStorage} from '../common/localStorage';
import Popover from "@cloudscape-design/components/popover";
import Button from "@cloudscape-design/components/button";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Icon from "@cloudscape-design/components/icon";
import Link from "@cloudscape-design/components/link";

const COLUMN_DEFINITIONS = [
    {
        id: "userName",
        header: "User Name",
        sortingField: "userName",
        cell: (d) => {
            if (d.status !== undefined && d.status.length > 0 && d.status !== "Done") {
                return "Processing..."
            } else {
                return <Link href={`/dashboard/${d.results_id}`}>{d.userName}</Link>
            }
        },
        minWidth: 160
    },
    {
        id: "group",
        header: "Group",
        sortingField: "group",
        cell: (d) => {
            console.log("Group data: ", d);
            if (d.status !== undefined && d.status.length > 0 && d.status !== "Done") {
                return "Processing..."
            } else {
                return d.group
            }
        }
    },
    {
        id: "call_timestamp",
        header: "Call Timestamp",
        sortingField: "call_timestamp",
        cell: (d) => {
            return Formatter.Timestamp(d.call_timestamp);
        },
        minWidth: 160
    },
    {
        id: "Call Date",
        header: "Call Date",
        sortingField: "date",
        cell: (d) => {
            return Formatter.Date(d.call_timestamp);
        },
        minWidth: 160
    },
    {
        id: "status",
        header: "Status",
        cell: (d) => d.status,
        isRowHeader: true,
        sortingField: "status",
        minWidth: 130
    },
    {
        id: "duration",
        header: <div className="col-header-wrapper text-left">Duration</div>,
        cell: (d) => Formatter.Time(d.duration),
        minWidth: 130,
    },
    {
        id: "callerSentimentScore",
        header: "Cust Sent",
        sortingField: "callerSentimentScore",
        cell: (d) => (
            <div className="d-flex justify-content-evenly">
                <SentimentIcon score={d?.callerSentimentScore}/>
                <TrendIcon trend={d.callerSentimentChange}/>
            </div>
        ),
        minWidth: 130
    },
    {
        id: "apptFirm",
        header: "Appt FIRM",
        cell: (d) => d.appointmentBooked,
        sortingField: "apptFirm",
        minWidth: 100
    },
    {
        id: "speaking",
        header: "Speaking",
        cell: (d) => d.speaking,
        minWidth: 130
    },
    {
        id: "dealerNameAcc",
        header: "Dealer Name Acc",
        cell: (d) => d.dealerNameAccuracy,
        minWidth: 130
    },
    {
        id: "selfConfidence",
        header: "Self Confidence",
        cell: (d) => d.selfConfidence,
        minWidth: 130
    },
    {
        id: "objectionsHandling",
        header: "Objections Handling",
        cell: (d) => d.objectionsHandling,
        minWidth: 130
    },
    {
        id: "activeListening",
        header: "Active Listening",
        cell: (d) => d.activeListening,
        minWidth: 130
    },
    {
        id: "fillerWords",
        header: "Filler Words",
        cell: (d) => d.fillerWords,
        minWidth: 130
    },
    {
        id: "usageOfTitles",
        header: "Usage Of Titles",
        cell: (d) => d.usageOfTitles,
        minWidth: 130
    },
    {
        id: "appreciationWordCount",
        header: "Appreciation Word Count",
        cell: (d) => d.appreciationWordCount,
        minWidth: 130
    },
    {
        id: "voicemail",
        header: "Voicemail",
        cell: (d) => d.voicemail,
        minWidth: 130
    },
    {
        id: "summary_summary",
        header: "Summary",
        cell: (d) => {
            if (d.summary_summary !== undefined) {
                return (
                    <Popover
                        dismissButton={false}
                        position="top"
                        size="large"
                        triggerType="text"
                        content={d.summary_summary}
                    >
                        {(d.summary_summary.length > 20 ? d.summary_summary.substring(0, 20) + "..." : d.summary_summary)}
                    </Popover>
                    /*
                    <ExpandableSection headerText={(d.summary_summary.length > 50 ? d.summary_summary.substring(0,50) + "..." : d.summary_summary )}>
                      {d.summary_summary}
                    </ExpandableSection>*/
                )
            }
            return 'n/a';
        },
        minWidth: 200
    }
];

const getMatchesCountText = function getMatchesCountText(count) {
    return count === 1 ? `1 match` : `${count} matches`;
}

const Loading = () =>
    COLUMN_DEFINITIONS.map((c, i) => (
        <td key={i}>
            <Placeholder/>
        </td>
    ));

const NoMatches = ({children}) => (
    <tr>
        <td colSpan={COLUMN_DEFINITIONS.length}>
            <div className="d-flex justify-content-center py-4">{children}</div>
        </td>
    </tr>
);

export const ContactTable = ({data = [], loading = false, empty, header, variant = 'embedded'}) => {
    const history = useHistory();

    const [preferences, setPreferences] = useLocalStorage(
        'contact-table-preferences',
        DEFAULT_PREFERENCES,
    );

    const onClick = (e) => {
        console.log('click: ', e);
        history.push(`/dashboard/${e.detail.item.key}`);
    };

    const [
        callQuery,
        setCallQuery
    ] = React.useState({
        tokens: [],
        operation: "and"
    });


    const {items, actions, filteredItemsCount, collectionProps, paginationProps, propertyFilterProps} = useCollection(
        data,
        {
            propertyFiltering: {
                filteringProperties: [
                    {
                        key: "userName",
                        operators: ["=", "!=", ":", "!:"],
                        propertyLabel: "User Name",
                        groupValuesLabel: "User Names"
                    },
                    {
                        key: "group",
                        operators: ["=", "!=", ":", "!:"],
                        propertyLabel: "Group",
                        groupValuesLabel: "Groups"
                    },
                    {
                        key: "call_timestamp",
                        defaultOperator: '>',
                        operators: ['<', '<=', '>', '>='].map(operator => ({
                            operator,
                            form: DateTimeForm,
                            format: formatDateTime,
                            match: 'datetime',
                        })),
                        propertyLabel: "Call Timestamp",
                        groupValuesLabel: "Call Timestamps"
                    },
                    {
                        key: "duration",
                        defaultOperator: '>',
                        operators: ['<', '<=', '>', '>='],
                        propertyLabel: "Duration",
                        groupValuesLabel: "Durations"
                    },
                    {
                        key: "callerSentimentScore",
                        defaultOperator: '>',
                        operators: ['<', '<=', '>', '>='],
                        propertyLabel: "Cust Sent",
                        groupValuesLabel: "Cust Sents"
                    },
                ],
                empty: (
                    <div>No Calls.</div>
                ),
                noMatch: (
                    <div>No matches.</div>
                )
            },
            pagination: {pageSize: 100000000},
            sorting: {},
            selection: {},
        });

    return (
        <Table
            {...collectionProps}
            header={header}
            variant={variant}
            columnDefinitions={COLUMN_DEFINITIONS}
            columnDisplay={preferences.contentDisplay}
            items={items}
            //pagination={<Pagination {...paginationProps} />}
            resizableColumns={true}
            loadingText="Loading Calls"
            // onSelectionChange={onClick}
            // onRowClick={onClick}
            // selectionType="single"
            stickyHeader={true}
            stickyColumns={{first: 2, last: 0}}
            filter={
                <PropertyFilter
                    {...propertyFilterProps}
                    /*onChange={({ detail }) => {
                      console.log(detail);
                      //setCallQuery(detail);
                    }}*/
                    //query={callQuery}
                    i18nStrings={{
                        filteringAriaLabel: "your choice",
                        dismissAriaLabel: "Dismiss",
                        filteringPlaceholder: "Find calls",
                        groupValuesText: "Values",
                        groupPropertiesText: "Properties",
                        operatorsText: "Operators",
                        operationAndText: "and",
                        operationOrText: "or",
                        operatorLessText: "Less than",
                        operatorLessOrEqualText: "Less than or equal",
                        operatorGreaterText: "Greater than",
                        operatorGreaterOrEqualText:
                            "Greater than or equal",
                        operatorContainsText: "Contains",
                        operatorDoesNotContainText: "Does not contain",
                        operatorEqualsText: "Equals",
                        operatorDoesNotEqualText: "Does not equal",
                        editTokenHeader: "Edit filter",
                        propertyText: "Property",
                        operatorText: "Operator",
                        valueText: "Value",
                        cancelActionText: "Cancel",
                        applyActionText: "Apply",
                        allPropertiesLabel: "All properties",
                        tokenLimitShowMore: "Show more",
                        tokenLimitShowFewer: "Show fewer",
                        clearFiltersText: "Clear filters",
                        removeTokenButtonAriaLabel: token =>
                            `Remove token ${token.propertyKey} ${token.operator} ${token.value}`,
                        enteredTextLabel: text => `Use: "${text}"`
                    }}
                    countText={getMatchesCountText(filteredItemsCount)}
                    expandToViewport={true}
                />
            }

            preferences={
                <ContactTablePreferences preferences={preferences} setPreferences={setPreferences}/>
            }
            visibleColumns={['jobName', ...preferences.visibleContent]}
        />
    );
};
